/**
 * @fileoverview useCaseIngestionAssist - Central hook for the assist system.
 *
 * Responsibilities:
 * - Fetches the import artifact via RTK Query.
 * - Merges imported values with current saved/form data.
 * - Derives per-step assist summaries with unresolved gap counts.
 * - Exposes current-step rows for the panel.
 * - Reports schema support status.
 *
 * Gap warnings are derived only — never persisted.
 */
import { useMemo } from 'react';

import type { CaseIngestionImport } from '@/services/types/case-ingestion-assist-types';

import type { ImportPayload } from '@/services/types/case-ingestion-assist-types';

import { UNIFIED_STEPS } from '../constants';
import { REGISTRY_BY_STEP } from './fieldRegistry';
import type {
  CaseIngestionAssistState,
  StepAssistSummary,
} from './types';
import { buildFieldRow, isUnresolved } from './utils';

/**
 * Map step IDs to the payload section(s) that contain source_urls.
 * Some steps draw from multiple sections (e.g. basics uses both case_info and client_info).
 */
const STEP_TO_PAYLOAD_SECTIONS: Record<string, (keyof ImportPayload)[]> = {
  basics: ['case_info', 'client_info'],
  case_info: ['case_info'],
  client_info: ['client_info'],
  liability: ['liability'],
  treatment: ['treatment'],
  coverage: ['coverage'],
};

function getSourceUrlsForStep(stepId: string, payload: ImportPayload): string[] {
  const sectionKeys = STEP_TO_PAYLOAD_SECTIONS[stepId];
  if (!sectionKeys) return [];
  const urls = new Set<string>();
  sectionKeys.forEach((key) => {
    const section = payload[key];
    if (section && typeof section === 'object' && 'source_urls' in section) {
      const sectionUrls = (section as { source_urls?: string[] }).source_urls;
      if (Array.isArray(sectionUrls)) {
        sectionUrls.forEach((url) => urls.add(url));
      }
    }
  });
  return Array.from(urls);
}

/**
 * Current values for each step, keyed by field ID.
 * Passed in by the consumer so the hook stays data-source agnostic.
 */
export type CurrentValueMap = Record<string, unknown>;

interface UseCaseIngestionAssistOptions {
  /** The import artifact (null if none exists) */
  importData: CaseIngestionImport | null | undefined;
  /** Whether the import query is loading */
  isLoading: boolean;
  /** Current step ID from the wizard */
  currentStepId: string;
  /** Map of fieldId → current value from saved data + active form state */
  currentValues: CurrentValueMap;
}

/**
 * Central assist hook. Derives all assist state from import artifact + current values.
 */
export function useCaseIngestionAssist({
  importData,
  isLoading,
  currentStepId,
  currentValues,
}: UseCaseIngestionAssistOptions): CaseIngestionAssistState {
  const hasImport = !!importData;

  const stepSummaries = useMemo(() => {
    if (!importData?.payload) return {};

    const { payload } = importData;

    return Object.keys(REGISTRY_BY_STEP).reduce<Record<string, StepAssistSummary>>(
      (summaries, stepId) => {
        const entries = REGISTRY_BY_STEP[stepId];
        const stepConfig = UNIFIED_STEPS.find((s) => s.id === stepId);
        const stepLabel = stepConfig?.shortLabel ?? stepId;

        const rows = entries.map((entry) =>
          buildFieldRow(entry, payload, currentValues[entry.fieldId]),
        );

        const applicableRows = rows.filter((r) => r.status !== 'not_applicable');
        const unresolvedRows = applicableRows.filter(isUnresolved);

        summaries[stepId] = {
          stepId,
          stepLabel,
          totalFields: applicableRows.length,
          unresolvedCount: unresolvedRows.length,
          unresolvedHighCount: unresolvedRows.filter((r) => r.severity === 'high').length,
          rows,
          sourceUrls: getSourceUrlsForStep(stepId, payload),
        };

        return summaries;
      },
      {},
    );
  }, [importData, currentValues]);

  const currentStepSummary = stepSummaries[currentStepId] ?? null;

  const totalUnresolvedHighCount = useMemo(() => {
    return Object.values(stepSummaries).reduce(
      (sum, s) => sum + s.unresolvedHighCount,
      0,
    );
  }, [stepSummaries]);

  return {
    hasImport,
    isLoading,
    importedAt: importData?.imported_at ?? null,
    sourceNotes: importData?.payload?.source_notes ?? null,
    stepSummaries,
    currentStepSummary,
    totalUnresolvedHighCount,
  };
}
